/* Unipile vault — DSN+key, accounts-picker, webhook & agent-bindings. */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { CheckCircle2, RefreshCw, Send, Loader2, Mail, Linkedin, MessageCircle, Webhook, Copy, Eye, EyeOff, Sparkles, Bot } from "lucide-react";
import { AdvancedSection, SubSection, AccountSelect } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  Unipile vault — key + DSN + account-picker + tiers + webhook     */
/* ----------------------------------------------------------------- */
function UnipileVault({ cfg, tiers, webhookUrl, onSaved }) {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [dsn, setDsn] = useState(cfg.dsn || "");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [accounts, setAccounts] = useState(null);
    const [loadingAcc, setLoadingAcc] = useState(false);
    const settings = cfg.settings || {};
    const [linkedinAcc, setLinkedinAcc] = useState(settings.linkedin_account_id || "");
    const [emailAcc, setEmailAcc]       = useState(settings.email_account_id || "");
    const [whatsappAcc, setWhatsappAcc] = useState(settings.whatsapp_account_id || "");
    const [linkedinTier, setLinkedinTier] = useState(settings.linkedin_tier || "premium_business");
    const [emailCap, setEmailCap]         = useState(settings.email_daily_cap || 500);
    const [whatsappCap, setWhatsappCap]   = useState(settings.whatsapp_daily_cap || 200);
    // Per-agent verzendaccounts — sub-agents kunnen via een eigen Unipile-
    // account versturen (bv. andere LinkedIn-persona per agent).
    const OUTREACH_AGENTS = [
        { id: "sales_outreach_agent", label: "Sales Outreach" },
        { id: "candidate_engagement_agent", label: "Candidate Engagement" },
    ];
    const [agentBindings, setAgentBindings] = useState(settings.agent_bindings || {});
    // Re-sync met opgeslagen settings zodra cfg (async) binnenkomt of ververst —
    // vult alleen lege velden, overschrijft nooit lokale keuzes van de gebruiker.
    useEffect(() => {
        const s = cfg.settings || {};
        setLinkedinAcc((v) => v || s.linkedin_account_id || "");
        setEmailAcc((v) => v || s.email_account_id || "");
        setWhatsappAcc((v) => v || s.whatsapp_account_id || "");
    }, [cfg]);
    const setBinding = (agentId, field, value) => {
        setAgentBindings((prev) => {
            const next = { ...prev, [agentId]: { ...(prev[agentId] || {}), [field]: value } };
            if (!value) delete next[agentId][field];
            if (Object.keys(next[agentId]).length === 0) delete next[agentId];
            return next;
        });
    };
    const [test, setTest] = useState({ channel: "linkedin", to: "", text: "Test van Orchestrator · Unipile-integratie werkt." });
    const [testBusy, setTestBusy] = useState(false);
    const [webhookBusy, setWebhookBusy] = useState(false);

    const saveCredentials = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.key_required_first")); return; }
        setBusy(true);
        try {
            const payload = { dsn };
            if (apiKey.trim()) payload.api_key = apiKey;
            await api.patch("/integrations/unipile", payload);
            toast.success(t("integr.unipile_saved"));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };

    const loadAccounts = async () => {
        setLoadingAcc(true);
        try {
            const r = await api.get("/integrations/unipile/accounts");
            setAccounts(r.data.accounts || []);
            toast.success(t("integr.accounts_found", { n: r.data.accounts?.length || 0 }));
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.accounts_failed")); }
        finally { setLoadingAcc(false); }
    };

    const saveAccountSelection = async () => {
        setBusy(true);
        try {
            await api.patch("/integrations/unipile", {
                settings: {
                    ...settings,
                    linkedin_account_id: linkedinAcc,
                    email_account_id: emailAcc,
                    whatsapp_account_id: whatsappAcc,
                    linkedin_tier: linkedinTier,
                    email_daily_cap: Number(emailCap) || 500,
                    whatsapp_daily_cap: Number(whatsappCap) || 200,
                    agent_bindings: agentBindings,
                },
            });
            toast.success(t("integr.mapping_saved"));
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };

    const runTest = async () => {
        if (!test.to.trim()) { toast.error(t("integr.add_recipient")); return; }
        let account_id = test.channel === "linkedin" ? (linkedinAcc || settings.linkedin_account_id)
            : test.channel === "email" ? (emailAcc || settings.email_account_id)
            : (whatsappAcc || settings.whatsapp_account_id);
        if (!account_id) {
            // Auto-herstel: laad accounts en kies automatisch het eerste account van dit kanaal.
            try {
                const r = await api.get("/integrations/unipile/accounts");
                const accs = r.data.accounts || [];
                setAccounts(accs);
                const match = accs.find((a) => a.channel === test.channel);
                if (match) {
                    account_id = match.id;
                    if (test.channel === "linkedin") setLinkedinAcc(match.id);
                    else if (test.channel === "email") setEmailAcc(match.id);
                    else setWhatsappAcc(match.id);
                    toast.info(t("integr.auto_selected_account", { name: match.name }));
                }
            } catch (e) { /* accounts ophalen faalde — foutmelding hieronder */ }
        }
        if (!account_id) { toast.error(t("integr.no_account_for_channel", { channel: test.channel })); return; }
        setTestBusy(true);
        try {
            const r = await api.post("/integrations/unipile/test", {
                channel: test.channel,
                account_id,
                to: test.to,
                text: test.text,
                subject: "Orchestrator test",
            });
            toast.success(t("integr.sent_check_inbox"));
            console.log("Unipile test result:", r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.test_failed")); }
        finally { setTestBusy(false); }
    };

    const registerWebhook = async () => {
        setWebhookBusy(true);
        try {
            const r = await api.post("/integrations/unipile/register-webhook");
            if (r.data.already_registered) {
                toast.success(t("integr.webhook_already"));
            } else {
                toast.success(t("integr.webhook_registered"));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.webhook_failed")); }
        finally { setWebhookBusy(false); }
    };

    const copyWebhook = () => {
        if (!webhookUrl) return;
        copyToClipboard(webhookUrl, t("integr.webhook_copied"));
    };

    return (
        <div className="space-y-5">
            {/* 1. Credentials */}
            <SubSection title={`1. ${t("integr.sec_credentials")}`} testid="unipile-credentials">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">DSN</label>
                        <input value={dsn} onChange={(e) => setDsn(e.target.value)}
                            placeholder="https://api34.unipile.com:16474"
                            data-testid="unipile-dsn-input"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">API Key</label>
                        <div className="mt-1 relative">
                            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                                placeholder={cfg.api_key_masked || t("integr.unipile_key_ph")}
                                type={reveal ? "text" : "password"}
                                data-testid="unipile-key-input"
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-3 pr-9 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                            <button type="button" onClick={() => setReveal((r) => !r)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--jr-text-3)] hover:text-white">
                                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>
                </div>
                <button onClick={saveCredentials} disabled={busy} data-testid="unipile-save-credentials"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                </button>
            </SubSection>

            {/* Secties 2-5 zijn geavanceerd: accounts, limieten, webhook, test-send */}
            <AdvancedSection id="unipile-config" label={t("integr.unipile_advanced")}>

            {/* 2. Accounts picker */}
            <SubSection title={`2. ${t("integr.sec_accounts")}`}
                action={
                    <button onClick={loadAccounts} disabled={loadingAcc} data-testid="unipile-load-accounts"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                        {loadingAcc ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} {t("common.refresh")}
                    </button>
                }
                testid="unipile-accounts">
                {!accounts ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.accounts_hint")}</p>
                ) : (
                    <div className="grid sm:grid-cols-3 gap-3">
                        <AccountSelect channel="linkedin" accounts={accounts.filter(a => a.channel === "linkedin")}
                            value={linkedinAcc} onChange={setLinkedinAcc} />
                        <AccountSelect channel="email" accounts={accounts.filter(a => a.channel === "email")}
                            value={emailAcc} onChange={setEmailAcc} />
                        <AccountSelect channel="whatsapp" accounts={accounts.filter(a => a.channel === "whatsapp")}
                            value={whatsappAcc} onChange={setWhatsappAcc} />
                    </div>
                )}
                {accounts && accounts.length > 0 && (
                    <div className="mt-4 rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/40 p-3.5"
                        data-testid="unipile-agent-bindings">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                            <Bot size={11} /> {t("integr.agent_bindings_title")}
                        </div>
                        <p className="text-[11px] text-[var(--jr-text-3)] mt-1 mb-3">{t("integr.agent_bindings_hint")}</p>
                        <div className="space-y-2.5">
                            {OUTREACH_AGENTS.map((ag) => (
                                <div key={ag.id} className="grid sm:grid-cols-[140px_1fr_1fr] gap-2 items-center">
                                    <div className="text-xs text-white font-medium">{ag.label}</div>
                                    <select value={(agentBindings[ag.id] || {}).linkedin_account_id || ""}
                                        onChange={(e) => setBinding(ag.id, "linkedin_account_id", e.target.value)}
                                        data-testid={`agent-binding-li-${ag.id}`}
                                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none">
                                        <option value="">{`LinkedIn: ${t("integr.agent_binding_default")}`}</option>
                                        {accounts.filter((a) => a.channel === "linkedin").map((a) => (
                                            <option key={a.id} value={a.id}>{`LinkedIn: ${a.name}`}</option>
                                        ))}
                                    </select>
                                    <select value={(agentBindings[ag.id] || {}).email_account_id || ""}
                                        onChange={(e) => setBinding(ag.id, "email_account_id", e.target.value)}
                                        data-testid={`agent-binding-mail-${ag.id}`}
                                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none">
                                        <option value="">{`E-mail: ${t("integr.agent_binding_default")}`}</option>
                                        {accounts.filter((a) => a.channel === "email").map((a) => (
                                            <option key={a.id} value={a.id}>{`E-mail: ${a.name}`}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </SubSection>

            {/* 3. Rate limits */}
            <SubSection title={`3. ${t("integr.sec_rate_limits")}`} testid="unipile-rate-limits">
                <div className="grid sm:grid-cols-3 gap-3">
                    {/* LinkedIn tier */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                            <Linkedin size={11} /> {t("integr.linkedin_tier")}
                        </label>
                        <select value={linkedinTier} onChange={(e) => setLinkedinTier(e.target.value)}
                            data-testid="unipile-linkedin-tier"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <optgroup label={t("integr.tier_standard")}>
                                <option value="basic">{`Free LinkedIn — 20 DMs/${t("integr.day_unit")}`}</option>
                                <option value="premium_career">{`Premium Career — 50 DMs/${t("integr.day_unit")}`}</option>
                                <option value="premium_business">{`Premium Business — 80 DMs/${t("integr.day_unit")}`}</option>
                            </optgroup>
                            <optgroup label="Sales">
                                <option value="sales_navigator">{`Sales Navigator — 100 DMs/${t("integr.day_unit")} · 50 InMails/${t("integr.month_unit")}`}</option>
                            </optgroup>
                            <optgroup label={t("integr.tier_recruiter")}>
                                <option value="recruiter_lite">{`Recruiter Lite — 30 InMails/${t("integr.month_unit")}`}</option>
                                <option value="recruiter">{`Recruiter — 150 InMails/${t("integr.month_unit")} · max throughput`}</option>
                            </optgroup>
                        </select>
                        {tiers[linkedinTier] && (
                            <p className="mt-1 text-[10px] text-[var(--jr-text-3)] leading-tight">{tiers[linkedinTier].description}</p>
                        )}
                        {(linkedinTier === "recruiter" || linkedinTier === "recruiter_lite") && (
                            <p className="mt-1.5 text-[10px] text-[var(--jr-amber)] font-mono">
                                <Sparkles size={9} className="inline mr-1"/>
                                {t("integr.recruiter_inmail_hint")}
                            </p>
                        )}
                    </div>
                    {/* Email cap */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                            <Mail size={11} /> {t("integr.email_cap")}
                        </label>
                        <input type="number" min={1} max={5000} value={emailCap} onChange={(e) => setEmailCap(e.target.value)}
                            data-testid="unipile-email-cap"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">{t("integr.gmail_limit")}</p>
                    </div>
                    {/* WhatsApp cap */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                            <MessageCircle size={11} /> {t("integr.whatsapp_cap")}
                        </label>
                        <input type="number" min={1} max={2000} value={whatsappCap} onChange={(e) => setWhatsappCap(e.target.value)}
                            data-testid="unipile-whatsapp-cap"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">{t("integr.whatsapp_limit")}</p>
                    </div>
                </div>
                <button onClick={saveAccountSelection} disabled={busy} data-testid="unipile-save-mapping"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("integr.save_mapping")}
                </button>
            </SubSection>

            {/* 4. Webhooks */}
            <SubSection title={`4. ${t("integr.sec_webhook")}`}
                action={
                    <button onClick={registerWebhook} disabled={webhookBusy || !cfg.configured} data-testid="unipile-register-webhook"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 disabled:opacity-50">
                        {webhookBusy ? <Loader2 size={10} className="animate-spin" /> : <Webhook size={10} />} {t("integr.register_unipile")}
                    </button>
                }
                testid="unipile-webhook">
                <div className="flex items-center gap-2">
                    <code className="flex-1 text-[11px] font-mono text-[var(--jr-text-2)] bg-[var(--jr-surface-2)] border border-white/10 rounded px-3 py-2 truncate">
                        {webhookUrl || t("integr.public_url_missing")}
                    </code>
                    <button onClick={copyWebhook} data-testid="copy-webhook-url"
                        className="p-2 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                        <Copy size={12} />
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)] leading-snug">
                    {t("integr.webhook_events_hint")}
                </p>
            </SubSection>

            {/* 5. Test send */}
            <SubSection title={`5. ${t("integr.sec_test_send")}`} testid="unipile-test">
                <div className="grid sm:grid-cols-4 gap-3">
                    <select value={test.channel} onChange={(e) => setTest({ ...test, channel: e.target.value })}
                        data-testid="test-channel"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                        <option value="linkedin">LinkedIn DM</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                    </select>
                    <input value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })}
                        placeholder={test.channel === "linkedin" ? "https://linkedin.com/in/handle" : test.channel === "email" ? "iemand@firma.com" : "+32475..."}
                        data-testid="test-to"
                        className="sm:col-span-2 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                    <button onClick={runTest} disabled={testBusy} data-testid="test-send"
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {testBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Test
                    </button>
                </div>
                <textarea value={test.text} onChange={(e) => setTest({ ...test, text: e.target.value })}
                    rows={2} placeholder={t("integr.text_ph")} data-testid="test-text"
                    className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
            </SubSection>

            </AdvancedSection>
        </div>
    );
}

export default UnipileVault;
